/**
 * Owner: Masa Bayu
 * File: AuthorSecurityJourneyPanel.tsx
 * Date:Time: 2026-09-16 15:20
 * Description: T6-E7 author security journey UI at settings — compose E1–E6.
 */

import { useId, useState } from 'react';
import {
  getDemoAuthorJourney,
  resetDemoAuthorJourney,
  runDemoAuthorJourney,
} from './demo-author-journey';

export interface AuthorSecurityJourneyPanelProps {
  readonly onNavigate: (path: string) => void;
}

export function AuthorSecurityJourneyPanel({ onNavigate }: AuthorSecurityJourneyPanelProps) {
  const statusId = useId();
  const controlsId = useId();
  const [status, setStatus] = useState('T6-E7 vertical journey — security boundaries only.');
  const [, setRevision] = useState(0);
  const journey = getDemoAuthorJourney();

  const refresh = (nextStatus: string) => {
    setStatus(nextStatus);
    setRevision((value) => value + 1);
  };

  return (
    <section className="route-panel author-settings" aria-labelledby="author-settings-title">
      <h2 id="author-settings-title">Author settings · security journey</h2>
      <p>
        Compose E1–E6 surfaces under T6-E7 security. QXWriter remains authoring authority; Admin
        review stays T7.
      </p>

      <p id={statusId} role="status" aria-live="polite" className="author-settings__status">
        {status}
        {journey !== null
          ? ` · state=${journey.state} · steps=${String(journey.steps.length)}`
          : ''}
      </p>

      <div
        id={controlsId}
        role="group"
        aria-label="Author security journey actions"
        className="author-settings__controls"
      >
        <button
          type="button"
          onClick={() => {
            refresh(runDemoAuthorJourney(true));
          }}
        >
          Run full journey
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(runDemoAuthorJourney(false));
          }}
        >
          Run without sales chain
        </button>
        <button
          type="button"
          onClick={() => {
            resetDemoAuthorJourney();
            refresh('Journey cleared.');
            setRevision((value) => value + 1);
          }}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={() => {
            onNavigate('/author');
          }}
        >
          Dashboard
        </button>
      </div>

      {journey !== null ? (
        <ol aria-label="Author integration journey steps" className="author-settings__steps">
          {journey.steps.map((step) => (
            <li key={step.step}>
              {step.step}: {step.ok ? 'ok' : 'blocked'} — {step.detail}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
